// src/ui/components/connectivityBanner.ts — Offline & Connectivity Observer (T-9.1.3, NFR-PWA-002)
// Provides reassuring naturalist feedback when Tudor is practicing away from internet access.

import { tokens } from '../designTokens';
import { soundEngine } from '@audio/soundEngine';

export interface ConnectivityBannerOptions {
  onStatusChange?: (isOnline: boolean) => void;
}

export class ConnectivityBanner {
  private container: HTMLElement;
  private isOnline: boolean;
  private onStatusChange?: (isOnline: boolean) => void;
  private restoreTimeoutId: ReturnType<typeof setTimeout> | null = null;
  private handleOnlineBound: () => void;
  private handleOfflineBound: () => void;

  constructor(options: ConnectivityBannerOptions = {}) {
    this.container = document.createElement('div');
    this.container.id = 'connectivity-banner-root';
    this.container.className = 'connectivity-banner';
    this.onStatusChange = options.onStatusChange;

    this.isOnline = typeof navigator !== 'undefined' ? navigator.onLine : true;

    this.handleOnlineBound = () => this.handleOnline();
    this.handleOfflineBound = () => this.handleOffline();

    if (typeof window !== 'undefined') {
      window.addEventListener('online', this.handleOnlineBound);
      window.addEventListener('offline', this.handleOfflineBound);
    }

    this.render();
  }

  getElement(): HTMLElement {
    return this.container;
  }

  getIsOnline(): boolean {
    return this.isOnline;
  }

  private handleOffline(): void {
    this.isOnline = false;
    if (this.restoreTimeoutId) {
      clearTimeout(this.restoreTimeoutId);
      this.restoreTimeoutId = null;
    }
    this.render();
    if (this.onStatusChange) {
      this.onStatusChange(false);
    }
  }

  private handleOnline(): void {
    this.isOnline = true;
    soundEngine.playSuccess();
    this.renderRestored();
    if (this.onStatusChange) {
      this.onStatusChange(true);
    }

    this.restoreTimeoutId = setTimeout(() => {
      this.render();
    }, 3500);
  }

  private render(): void {
    if (this.isOnline) {
      this.container.innerHTML = '';
      this.container.style.display = 'none';
      return;
    }

    this.container.style.display = 'block';
    this.container.innerHTML = `
      <div
        id="offline-status-bar"
        role="status"
        aria-live="polite"
        style="
          background: rgba(255, 246, 229, 0.96);
          border-bottom: 2px solid ${tokens.color.gildedBrass};
          color: ${tokens.color.morrisIndigo};
          padding: 8px 18px;
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 10px;
          font-size: 0.85rem;
          font-weight: 700;
          box-shadow: 0 2px 8px rgba(7, 20, 36, 0.08);
          animation: slideDownBanner 0.25s ease;
        "
      >
        <span style="font-size: 1.1rem;">⚓</span>
        <span>
          <strong>Offline Expedition Mode:</strong> All question sets, geometry sandboxes, and Socratic hints are available locally!
        </span>
      </div>
    `;
  }

  private renderRestored(): void {
    this.container.style.display = 'block';
    this.container.innerHTML = `
      <div
        id="online-restored-bar"
        role="status"
        aria-live="polite"
        style="
          background: rgba(235, 245, 238, 0.96);
          border-bottom: 2px solid ${tokens.color.seaweedSage};
          color: ${tokens.color.seaweedSage};
          padding: 8px 18px;
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 10px;
          font-size: 0.85rem;
          font-weight: 800;
          box-shadow: 0 2px 8px rgba(61, 90, 69, 0.1);
        "
      >
        <span style="font-size: 1.1rem;">🌊</span>
        <span>Ocean Signal Restored — Reconnected to Marine Observatory!</span>
      </div>
    `;
  }

  destroy(): void {
    if (typeof window !== 'undefined') {
      window.removeEventListener('online', this.handleOnlineBound);
      window.removeEventListener('offline', this.handleOfflineBound);
    }
    if (this.restoreTimeoutId) {
      clearTimeout(this.restoreTimeoutId);
    }
  }
}
